import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  addSelectItem,
  createColumn,
  createEditAction,
  createViewAction,
  formatDateTime,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_SYSTEM_ACCOUNTS } from "../../utils/urls";
import useGet from "../common/useGet";
import { API_SYSTEM_ACCOUNTS } from "../../utils/apis";

export const useSystemAccountManagements = () => {
  const tableColumns = () => [
    createColumn("account_name", true, "Account Name"),
    createColumn("account_type", true, "Account Type"),
    createColumn("account_balance", true, "Account Balance"),
    createColumn("actions", false),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => ({
      ...item,
      actions: (
        <FormActions
          actions={[
            createViewAction(URL_SYSTEM_ACCOUNTS, item?.id),
            createEditAction(URL_SYSTEM_ACCOUNTS, item?.id),
          ]}
        />
      ),
    }));
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "System Account Details",
          fields: {
            account_name: item?.account_name || "N/A",
            account_description: item?.account_description || "N/A",
            account_type: item?.account_type || "N/A",
            account_currency: item?.account_currency || "N/A",
            account_balance: item?.account_balance || "0",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item?.id || "N/A",
            created_by: item?.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`.trim()
              : "System",
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
            updated_at: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "N/A",
            updated_by: item?.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`.trim()
              : "Not updated",
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_SYSTEM_ACCOUNTS);
  // const activityFilter = "system_accounts";

  return {
    tableColumns,
    tableData,
    viewData,
    formRoute,
    // activityFilter,
  };
};

export const useSystemAccountsManagementForm = ({}: TFormHookParams = {}) => {
  const { loadData, loading, data } = useGet();

  useEffect(() => {
    loadData({ api: API_SYSTEM_ACCOUNTS });
  }, []);

  const sections: IFormSection[] = [
    {
      title: "System Account Details",
      fields: [
        {
          field_name: "account_name",
          label: "Account Name",
          type: "text",
          props: {
            required: true,
            placeholder: "Enter account name",
          },
        },
        {
          field_name: "account_description",
          label: "Account Description",
          type: "text",
          props: {
            placeholder: "Enter account description",
          },
        },
        {
          field_name: "account_type",
          label: "Account Type",
          type: "select",
          options_data: [
            addSelectItem({ label: "Commission", value: "COMMISSION" }),
            addSelectItem({ label: "Escrow", value: "ESCROW" }),
            addSelectItem({ label: "Suspense", value: "SUSPENSE" }),
          ],
        },
        {
          field_name: "account_balance",
          label: "Initial Balance",
          type: "number",
          props: {
            required: true,
            placeholder: "Enter initial balance",
          },
        },
      ],
    },
  ];

  const title = "System Account Management";
  const redirect = getRegister(URL_SYSTEM_ACCOUNTS);

  const onSubmit = (payload: any, id: string) => {
    if (id) {
      return {
        id,
        api: API_SYSTEM_ACCOUNTS,
        params: {
          ...payload,
        },
      };
    }
    return {
      api: API_SYSTEM_ACCOUNTS,
      params: {
        ...payload,
      },
    };
  };

  return {
    sections,
    title,
    redirect,
    loading,
    data,
    onSubmit,
  };
};
